import { useCallback, useMemo, useState } from 'react';
import {
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FiActivity, FiLoader } from 'react-icons/fi';
import {
  RegistryMetric,
  RegistryVintages,
  getExportRows,
  getVintages,
  listRegistryMetrics,
  runExportToCompletion,
} from '../../../api/registry';

/**
 * Explore the released hourly record in-app.
 *
 * Same endpoints as the download, and the rows come from the file the export
 * produced — so what is charted here is what a partner receives, not a parallel
 * calculation that could disagree with it.
 *
 * A year at a time: the released record runs to ~61k hours per generator, which
 * is a download, not a chart. Three questions this answers — "when does this
 * asset avoid emissions" (average day), "what did it do on a given day"
 * (drilldown), and "what did it do over the year" (daily totals).
 */

/** The metric the registry actually releases a REC against. */
const RELEASED_METRIC_KEY = 'avoided_co2e_kg';

/** Used until the catalogue loads, and if the catalogue call fails. */
const FALLBACK_METRIC: RegistryMetric = {
  key: RELEASED_METRIC_KEY,
  label: 'Climate change',
  unit: 'kg CO2-eq',
  family: 'endpoint',
  compartment: 'aggregate',
};

/**
 * Local STANDARD-time offset for a balancing authority, in hours from UTC.
 *
 * Standard time deliberately, not DST-aware. The model's own profiles carry no
 * DST (SAM/TMY are standard-time series, and the engine's time base uses fixed
 * standard offsets), so a DST-aware display would disagree with the data it is
 * describing and would smear an average-day chart that blends both halves of
 * the year. A fixed offset keeps the picture consistent and the caveat
 * statable, which is why the panel says so on screen.
 *
 * Null for an unrecognised BA — the toggle then stays on UTC rather than
 * inventing an offset.
 */
const BA_STANDARD_OFFSET: Record<string, number> = {
  // Eastern
  PJM: -5, ISNE: -5, NYIS: -5, MISO: -6, TVA: -6, SOCO: -6, DUK: -5, CPLE: -5,
  SC: -5, SCEG: -5, FPL: -5, FPC: -5, TEC: -5, JEA: -5, SEC: -5, AEC: -6,
  // Central
  SWPP: -6, SPP: -6, ERCO: -6, LGEE: -5, OVEC: -5,
  // Mountain
  PNM: -7, EPE: -7, PSCO: -7, WACM: -7, AZPS: -7, SRP: -7, TEPC: -7, WALC: -7,
  // Pacific
  CISO: -8, BANC: -8, IID: -8, LDWP: -8, TIDC: -8, BPAT: -8, PACW: -8,
  PGE: -8, PSEI: -8, SCL: -8, AVA: -8, CHPD: -8, DOPD: -8, GCPD: -8,
  NWMT: -7, IPCO: -7, PACE: -7, NEVP: -8,
};

export function standardOffsetFor(baCode?: string | null): number | null {
  if (!baCode) return null;
  const off = BA_STANDARD_OFFSET[baCode.toUpperCase()];
  return off === undefined ? null : off;
}

/**
 * Unit-prefix rule, not a per-metric lookup table.
 *
 * A year of avoided PM2.5 and a year of avoided CO2e differ by six orders of
 * magnitude, so a single fixed unit makes one of them unreadable. This shifts
 * kg to tonnes once the number is large enough to warrant it, which is a
 * property of the unit rather than of the metric — so it applies uniformly to
 * every kg-based indicator and cannot drift as metrics are added.
 */
export function scaleFor(unit: string, magnitude: number): { factor: number; unit: string } {
  if (unit.startsWith('kg ') && Math.abs(magnitude) >= 1e6) {
    return { factor: 1e-3, unit: `t ${unit.slice(3)}` };
  }
  return { factor: 1, unit };
}

const compact = (v: number) =>
  Math.abs(v) >= 1000
    ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : v.toLocaleString(undefined, { maximumSignificantDigits: 3 });

/**
 * The last calendar year the ledger has actually released hours in.
 *
 * `released_through` is an EXCLUSIVE bound, so the last released hour begins one
 * hour earlier — and it is that hour's year we want. Taking `year - 1` instead
 * only works when the frontier lands exactly on a year boundary; the moment a
 * frontier is trimmed mid-year (as MISO's was, to 2025-12-31T01:00Z, because the
 * artifact stops 23 hours short) it silently drops the whole partial final year
 * despite its thousands of released hours.
 */
export function lastReleasedYear(releasedThrough: string): number | null {
  const t = new Date(releasedThrough).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t - 3_600_000).getUTCFullYear();
}

interface Props {
  projectId: string;
  projectName: string;
  token: string | null;
}

type Row = Record<string, string>;

/** The row's timestamp shifted into the display timezone. */
const shiftedDate = (r: Row, shift: number): Date | null => {
  const raw = r.timestamp_utc ?? r.timestamp;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + shift * 3_600_000);
};

const num = (r: Row, k: string) => {
  const v = parseFloat(r[k]);
  return Number.isFinite(v) ? v : 0;
};

export interface DayTotal {
  date: string;
  gen: number;
  m: number;
}

export interface HourProfile {
  hour: string;
  gen: number;
  m: number;
  /** Lowest and highest value this hour took across the days in scope. */
  lo: number;
  hi: number;
  days: number;
}

/**
 * Daily totals, keyed by the date the hour falls on *in the display timezone*.
 *
 * The shift is applied before the date is taken, not after, which is the whole
 * point: for a US generator the last hours of a UTC day belong to the previous
 * local day, so bucketing on the raw UTC stamp would attribute an evening's
 * output to tomorrow. For a portfolio spanning several time zones that error
 * differs per asset, so it cannot be corrected downstream.
 */
export function dayTotals(rows: Row[], shift: number, metricKey: string): DayTotal[] {
  const by = new Map<string, { gen: number; m: number }>();
  for (const r of rows) {
    const sd = shiftedDate(r, shift);
    if (!sd) continue;
    const d = sd.toISOString().slice(0, 10);
    const cur = by.get(d) ?? { gen: 0, m: 0 };
    cur.gen += num(r, 'generation_mwh');
    cur.m += num(r, metricKey);
    by.set(d, cur);
  }
  return Array.from(by.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, gen: v.gen, m: v.m }));
}

/**
 * Hour-of-day profile over an optional set of days.
 *
 * `scopeDays === null` means every loaded day (the average-day view); a set
 * narrows it to one day or a brushed span. The min/max are carried alongside
 * the mean so that a span view shows the spread across days rather than
 * flattening an unusual day into the average.
 */
export function hourProfile(
  rows: Row[],
  shift: number,
  scopeDays: Set<string> | null,
  metricKey: string,
): HourProfile[] {
  const acc = Array.from({ length: 24 }, () => ({
    gen: 0, m: 0, n: 0, min: Infinity, max: -Infinity,
  }));
  for (const r of rows) {
    const d = shiftedDate(r, shift);
    if (!d) continue;
    if (scopeDays && !scopeDays.has(d.toISOString().slice(0, 10))) continue;
    const h = d.getUTCHours();
    const v = num(r, metricKey);
    const a = acc[h];
    a.gen += num(r, 'generation_mwh');
    a.m += v;
    a.n += 1;
    if (v < a.min) a.min = v;
    if (v > a.max) a.max = v;
  }
  return acc.map((a, h) => ({
    hour: String(h).padStart(2, '0'),
    gen: a.n ? a.gen / a.n : 0,
    m: a.n ? a.m / a.n : 0,
    lo: a.n && a.min !== Infinity ? a.min : 0,
    hi: a.n && a.max !== -Infinity ? a.max : 0,
    days: a.n,
  }));
}

export function TimeseriesExplorer({ projectId, projectName, token }: Props) {
  const [open, setOpen] = useState(false);
  const [vintages, setVintages] = useState<RegistryVintages | null>(null);
  const [catalogue, setCatalogue] = useState<RegistryMetric[]>([]);
  const [metricKey, setMetricKey] = useState<string>(RELEASED_METRIC_KEY);
  const [year, setYear] = useState<string>('');
  const [rows, setRows] = useState<Row[] | null>(null);
  // Display timezone. Local by default: an average-day chart is read as "when
  // does this asset generate", and a solar peak shown at 18:00 reads as wrong
  // even though it is correct UTC. The underlying data, the CSV download and
  // the API all stay UTC — this only changes how hours are labelled here.
  const [tz, setTz] = useState<'local' | 'utc'>('local');
  // Drilldown selection. A specific day wins over a brushed span; both are
  // cleared by the "Average day" reset.
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [brush, setBrush] = useState<{ s: number; e: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const offsetHours = standardOffsetFor(vintages?.ba_code);
  // Fall back to UTC when the BA is unknown rather than inventing an offset.
  const usingLocal = tz === 'local' && offsetHours !== null;
  const shift = usingLocal ? (offsetHours as number) : 0;
  const tzLabel = usingLocal ? 'local' : 'UTC';

  const metric = useMemo(
    () => catalogue.find((m) => m.key === metricKey) ?? FALLBACK_METRIC,
    [catalogue, metricKey],
  );
  const isReleasedMetric = metric.key === RELEASED_METRIC_KEY;
  // A metric the catalogue offers but this export does not carry would chart as
  // a flat zero line, which reads as "this hour avoided nothing" rather than
  // "this column is absent". Say which it is.
  const metricMissing = !!rows?.length && !(metric.key in rows[0]);

  // Offer only years the ledger has actually released, so the control cannot
  // ask for hours that do not exist.
  const years = useMemo(() => {
    if (!vintages?.released_through || !vintages?.segments?.length) return [];
    const first = new Date(vintages.segments[0].hour_start).getUTCFullYear();
    const last = lastReleasedYear(vintages.released_through);
    if (last === null) return [];
    const out: string[] = [];
    for (let y = last; y >= first; y--) out.push(String(y));
    return out;
  }, [vintages]);

  const openPanel = async () => {
    setOpen(true);
    if (vintages || !token) return;
    // The catalogue is a nicety; the released range is not. Let the metric list
    // fail back to CO2e rather than blocking the panel on it.
    listRegistryMetrics(token)
      .then(setCatalogue)
      .catch(() => setCatalogue([]));
    try {
      const v = await getVintages(token, projectId);
      setVintages(v);
      const last = v.released_through ? lastReleasedYear(v.released_through) : null;
      if (last) setYear(String(last));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load released range.');
    }
  };

  const resetScope = useCallback(() => {
    setSelectedDay(null);
    setBrush(null);
  }, []);

  const load = async () => {
    if (!token || !year) return;
    setBusy(true);
    setError(null);
    setRows(null);
    resetScope();
    setStatus('Building the series…');
    try {
      const result = await runExportToCompletion(
        token,
        {
          project_ids: [projectId],
          granularity: 'hourly',
          format: 'csv',
          lens: 'operational',
          // Every metric. Omitting `metrics` is the registry's default (all 22
          // indicators), which lets the selector below switch metric without a
          // refetch. Asking for 'endpoints' here was what limited the explorer
          // to four.
          start: `${year}-01-01T00:00:00Z`,
          end: `${Number(year) + 1}-01-01T00:00:00Z`,
        },
        { onProgress: (s) => setStatus(s === 'running' ? 'Building the series…' : 'Queued…') },
      );
      setStatus('Reading rows…');
      const data = await getExportRows(token, result.export_id, {
        projectId,
        limit: 8784,
      });
      setRows(data.rows);
      setStatus(`${data.rows_returned.toLocaleString()} hours loaded.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the series.');
      setStatus(null);
    } finally {
      setBusy(false);
    }
  };

  // Every day in the loaded year, in display-timezone terms. Also the index
  // space the brush selects over.
  const daily = useMemo(
    () => (rows ? dayTotals(rows, shift, metric.key) : []),
    [rows, shift, metric.key],
  );

  const spanStart = brush ? Math.max(0, Math.min(brush.s, daily.length - 1)) : 0;
  const spanEnd = brush ? Math.max(0, Math.min(brush.e, daily.length - 1)) : daily.length - 1;
  const brushed = !!brush && (spanStart > 0 || spanEnd < daily.length - 1);

  const mode: 'average' | 'day' | 'range' = selectedDay
    ? 'day'
    : brushed
      ? 'range'
      : 'average';

  // Which days the charts and stats describe. Null = the whole loaded year.
  const scopeDays = useMemo(() => {
    if (mode === 'day') return new Set([selectedDay as string]);
    if (mode === 'range') {
      return new Set(daily.slice(spanStart, spanEnd + 1).map((d) => d.date));
    }
    return null;
  }, [mode, selectedDay, daily, spanStart, spanEnd]);

  const scopeLabel =
    mode === 'day'
      ? (selectedDay as string)
      : mode === 'range'
        ? `${daily[spanStart]?.date} → ${daily[spanEnd]?.date}`
        : year;

  // The hour-of-day profile over the current scope. In `average` and `range`
  // modes this is a mean across days, with a min/max band so a single unusual
  // day is visible rather than averaged away. In `day` mode there is one value
  // per hour and the band is meaningless, so it is not drawn.
  const hourly = useMemo(
    () => (rows ? hourProfile(rows, shift, scopeDays, metric.key) : []),
    [rows, shift, scopeDays, metric.key],
  );

  const stats = useMemo(() => {
    if (!rows?.length) return null;
    let gen = 0;
    let m = 0;
    let hours = 0;
    for (const r of rows) {
      const d = shiftedDate(r, shift);
      if (!d) continue;
      if (scopeDays && !scopeDays.has(d.toISOString().slice(0, 10))) continue;
      gen += num(r, 'generation_mwh');
      m += num(r, metric.key);
      hours += 1;
    }
    if (!hours) return null;
    const peak = hourly.reduce((best, d, i) => (d.gen > hourly[best].gen ? i : best), 0);
    return {
      gen,
      m,
      intensity: gen > 0 ? m / gen : 0,
      peakHour: peak,
      hours,
    };
  }, [rows, shift, scopeDays, metric.key, hourly]);

  // Two scales: a year of avoided CO2e reads in tonnes, a single hour of it
  // does not. Sizing both off the same total would render one of them as zeroes.
  const totalScale = scaleFor(metric.unit, stats?.m ?? 0);
  const hourScale = scaleFor(
    metric.unit,
    hourly.reduce((mx, h) => Math.max(mx, Math.abs(h.hi), Math.abs(h.m)), 0),
  );
  const dayScale = scaleFor(
    metric.unit,
    daily.reduce((mx, d) => Math.max(mx, Math.abs(d.m)), 0),
  );

  const hourChart = useMemo(
    () =>
      hourly.map((h) => ({
        hour: h.hour,
        MWh: +h.gen.toFixed(2),
        [hourScale.unit]: +(h.m * hourScale.factor).toPrecision(4),
        min: +(h.lo * hourScale.factor).toPrecision(4),
        max: +(h.hi * hourScale.factor).toPrecision(4),
      })),
    [hourly, hourScale.unit, hourScale.factor],
  );

  const dayChart = useMemo(
    () =>
      daily.map((d) => ({
        date: d.date,
        MWh: +d.gen.toFixed(1),
        [dayScale.unit]: +(d.m * dayScale.factor).toPrecision(4),
      })),
    [daily, dayScale.unit, dayScale.factor],
  );

  const endpointOthers = catalogue.filter(
    (m) => m.family === 'endpoint' && m.key !== RELEASED_METRIC_KEY,
  );
  const midpoints = catalogue.filter((m) => m.family === 'midpoint');

  const btn =
    'inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors border border-quantum-500/50 text-quantum-400 hover:bg-quantum-500/10';

  if (!token) return null;
  if (!open) {
    return (
      <button className={btn} onClick={openPanel} type="button">
        <FiActivity className="w-4 h-4" />
        Explore hourly
      </button>
    );
  }

  // Rendered as an overlay, not inline. The trigger lives in a header button
  // row (`flex items-center gap-2 shrink-0`), and a full-width panel as a flex
  // item there pushes the header past the viewport — which showed up as a page
  // that scrolls sideways. It also left the charts with no definite width, so
  // Recharts' ResponsiveContainer measured 0 and drew axes with no series.
  // An overlay escapes that row and gets its size from the viewport instead.
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`Hourly record for ${projectName}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-full max-w-5xl rounded border border-quantum-500/40 bg-slate-900 p-4 text-sm text-slate-200 shadow-2xl">
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-quantum-300">
          Hourly record — {projectName}
        </div>
        <button
          className="text-slate-400 hover:text-slate-200 text-xs"
          onClick={() => setOpen(false)}
          type="button"
        >
          Close
        </button>
      </div>

      <p className="text-[11px] text-slate-500 mb-3">
        The released, operational-lens record — the same data the API returns.
        Positive = avoided.{' '}
        {usingLocal ? (
          <>
            Hours are shown in <span className="text-slate-300">local standard
            time</span> (UTC{offsetHours! >= 0 ? '+' : ''}{offsetHours}), with no
            daylight-saving shift, so an average day reads against the sun. The
            record itself — the API, the CSV download and the ledger — is always
            UTC hour-beginning.
          </>
        ) : (
          <>
            Hours are <span className="text-slate-300">UTC hour-beginning</span>,
            matching the API, the CSV download and the released ledger.
          </>
        )}
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="block">
          <span className="text-xs text-slate-400">Year</span>
          <select
            className="mt-1 bg-slate-800 border border-slate-700 rounded px-2 py-1"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {years.length === 0 && <option value="">no released years</option>}
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">Indicator</span>
          <select
            className="mt-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 max-w-xs"
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
            aria-label="Indicator"
          >
            {/* Grouped so the released REC metric cannot be mistaken for a peer
                of the other 21. The list itself comes from GET /registry/metrics,
                so a metric added server-side appears here — with its own unit —
                without a frontend change, and the UI cannot offer one the
                artifact lacks. */}
            <optgroup label="Released REC metric">
              <option value={RELEASED_METRIC_KEY}>
                {(catalogue.find((m) => m.key === RELEASED_METRIC_KEY) ?? FALLBACK_METRIC).label}
                {' '}(kg CO2-eq)
              </option>
            </optgroup>
            {endpointOthers.length > 0 && (
              <optgroup label="Other endpoints — context only">
                {endpointOthers.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label} ({m.unit})
                  </option>
                ))}
              </optgroup>
            )}
            {midpoints.length > 0 && (
              <optgroup label="Midpoint indicators — context only">
                {midpoints.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label} ({m.unit})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>

        <div>
          <span className="text-xs text-slate-400">Hours shown in</span>
          <div className="mt-1 flex rounded border border-slate-700 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setTz('local')}
              disabled={offsetHours === null}
              title={
                offsetHours === null
                  ? 'No timezone known for this balancing authority — showing UTC.'
                  : `Local standard time (UTC${offsetHours >= 0 ? '+' : ''}${offsetHours}). ` +
                    'Standard time year-round, with no daylight-saving shift, matching the ' +
                    'generation profile behind these figures.'
              }
              className={`px-2 py-1 ${
                usingLocal
                  ? 'bg-quantum-500/20 text-quantum-300'
                  : 'text-slate-400 hover:text-slate-200 disabled:opacity-40'
              }`}
            >
              Local
            </button>
            <button
              type="button"
              onClick={() => setTz('utc')}
              title="UTC — matches the API, the CSV download and the released ledger."
              className={`px-2 py-1 border-l border-slate-700 ${
                !usingLocal ? 'bg-quantum-500/20 text-quantum-300' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              UTC
            </button>
          </div>
        </div>
        <button className={btn} onClick={load} disabled={busy || !year} type="button">
          {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiActivity className="w-4 h-4" />}
          {busy ? 'Loading…' : 'Load'}
        </button>
        {status && !error && <span className="text-xs text-slate-400">{status}</span>}
        {error && <span className="text-xs text-amber-400">{error}</span>}
      </div>

      {metricMissing && (
        <p className="text-[11px] text-amber-400 mb-3">
          This export does not carry a <code>{metric.key}</code> column, so the
          charts below would read as zero. Re-load the year to fetch it.
        </p>
      )}

      {stats && (
        <>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="text-slate-400">Showing</span>
            <span className="text-slate-200 font-medium">
              {mode === 'day'
                ? `${scopeLabel} — a single day`
                : mode === 'range'
                  ? `${scopeLabel} — ${scopeDays?.size ?? 0} days`
                  : `${scopeLabel} — full year, averaged`}
            </span>
            {mode !== 'average' && (
              <button
                type="button"
                onClick={resetScope}
                className="text-quantum-400 hover:text-quantum-300 underline underline-offset-2"
              >
                Reset to average day
              </button>
            )}
            {mode === 'average' && (
              <span className="text-slate-500">
                — click a day below to drill in, or drag the handles to select a span
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
            <Stat label="Generation" value={`${compact(stats.gen)} MWh`} sub={scopeLabel} />
            <Stat
              label={`Avoided — ${metric.label}`}
              value={`${compact(stats.m * totalScale.factor)} ${totalScale.unit}`}
              sub={isReleasedMetric ? 'released REC metric' : 'context only'}
            />
            <Stat
              label="Intensity"
              value={`${compact(stats.intensity)} ${metric.unit}/MWh`}
              sub="generation-weighted"
            />
            <Stat
              label="Peak output"
              value={`${String(stats.peakHour).padStart(2, '0')}:00 ${tzLabel}`}
              sub={mode === 'day' ? 'this day' : 'average day'}
            />
          </div>

          <div className="mb-5">
            <div className="text-xs text-slate-400 mb-1">
              {mode === 'day'
                ? `${scopeLabel} by hour (${tzLabel})`
                : `Average day (${tzLabel} hours)`}
              {' — generation and avoided '}{metric.label.toLowerCase()}
              {mode === 'range' && ', with the min–max across the selected days'}
            </div>
            <ResponsiveContainer width="100%" height={210}>
              <ComposedChart data={hourChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="l" dataKey="MWh" fill="#0072CE" />
                <Bar yAxisId="r" dataKey={hourScale.unit} fill="#009639" />
                {mode !== 'day' && (
                  <Line
                    yAxisId="r"
                    type="monotone"
                    dataKey="max"
                    name="max across days"
                    stroke="#7AE1BF"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                )}
                {mode !== 'day' && (
                  <Line
                    yAxisId="r"
                    type="monotone"
                    dataKey="min"
                    name="min across days"
                    stroke="#63666A"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mb-4">
            <div className="text-xs text-slate-400 mb-1">
              Daily totals across {year} — click a day to drill in
            </div>
            <ResponsiveContainer width="100%" height={210}>
              <LineChart
                data={dayChart}
                onClick={(s: { activeLabel?: string | number }) => {
                  const label = s?.activeLabel;
                  if (typeof label !== 'string') return;
                  setSelectedDay((cur) => (cur === label ? null : label));
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} minTickGap={40} />
                <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {selectedDay && (
                  <ReferenceLine yAxisId="l" x={selectedDay} stroke="#59CBE8" strokeWidth={1.5} />
                )}
                <Line yAxisId="l" type="monotone" dataKey="MWh" stroke="#0072CE" dot={false} />
                <Line yAxisId="r" type="monotone" dataKey={dayScale.unit} stroke="#009639" dot={false} />
                <Brush
                  dataKey="date"
                  height={18}
                  travellerWidth={8}
                  stroke="#475569"
                  fill="#0f172a"
                  startIndex={spanStart}
                  endIndex={spanEnd}
                  onChange={(r: { startIndex?: number; endIndex?: number }) => {
                    if (r?.startIndex === undefined || r?.endIndex === undefined) return;
                    setBrush({ s: r.startIndex, e: r.endIndex });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

        </>
      )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded border border-slate-700 bg-slate-800/50 px-3 py-2">
      <div className="text-slate-400">{label}</div>
      <div className="text-slate-100 text-sm font-medium">{value}</div>
      {sub && <div className="text-slate-500 text-[10px]">{sub}</div>}
    </div>
  );
}
